/**
 * Realtor Messages (#332) — a log the realtor keeps of each conversation.
 *
 * The other person has no Finishd account and never sees this page. The
 * realtor messages them on WhatsApp or calls, and records here what was sent
 * and what they said back. Nothing is sent from Finishd.
 *
 * The open conversation lives in the URL (`?contact=<contactId>`), so other
 * pages can link straight to one, including a contact with no messages yet.
 * Desktop shows the inbox and the conversation side by side; below `md` it
 * shows one at a time with a back button.
 */

import { FileUp, MessageSquare, Plus, RefreshCw, Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ImportChatDialog } from "@/features/realtor/components/import/import-chat-dialog";
import { Conversation } from "@/features/realtor/components/messages/conversation";
import { NewConversationDialog } from "@/features/realtor/components/messages/new-conversation-dialog";
import { ThreadList } from "@/features/realtor/components/messages/thread-list";
import {
	useContact,
	useThreads,
} from "@/features/realtor/hooks/use-realtor-crm";
import { StudioButton } from "@/features/studio/theme";

export function RealtorMessagesPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [picking, setPicking] = useState(false);
	const [importing, setImporting] = useState(false);
	const selectedId = searchParams.get("contact");

	const threads = useThreads();
	const thread = threads.data?.find((item) => item.contact.id === selectedId);
	// A contact with no messages has no thread, so fetch it directly.
	const needsContact = Boolean(selectedId) && !threads.isPending && !thread;
	const contactQuery = useContact(needsContact ? selectedId : null);
	const contact = thread?.contact ?? contactQuery.data?.contact;

	const open = (contactId: string) => {
		setSearchParams({ contact: contactId });
		setPicking(false);
	};
	const close = () => setSearchParams({});

	const noThreads = threads.data?.length === 0 && !selectedId;

	return (
		<div className="bg-dashboard-surface min-h-screen space-y-6 p-4 sm:p-6">
			<header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div className="min-w-0">
					<p className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
						{t("realtor.messages.eyebrow")}
					</p>
					<h1
						className="mt-2 font-display text-3xl italic font-medium text-dashboard-accent-dark"
						style={{ lineHeight: 1.1 }}
					>
						{t("realtor.messages.title")}
					</h1>
					<p className="mt-2 max-w-2xl text-xs text-dashboard-muted">
						{t("realtor.messages.subtitle")}
					</p>
				</div>
				<StudioButton
					variant="secondary"
					size="sm"
					icon={FileUp}
					className="shrink-0 self-start"
					onClick={() => setImporting(true)}
				>
					{t("realtor.import.action")}
				</StudioButton>
			</header>

			{noThreads ? (
				<EmptyInbox
					onNewConversation={() => setPicking(true)}
					onImport={() => setImporting(true)}
				/>
			) : (
				<Card className="overflow-hidden rounded-xl border-dashboard-border bg-dashboard-bg shadow-[var(--dashboard-shadow-2)]">
					<div className="grid md:h-[calc(100dvh-14rem)] md:min-h-[520px] md:grid-cols-[320px_minmax(0,1fr)]">
						<section
							className={`min-h-0 min-w-0 md:border-r md:border-dashboard-border ${
								selectedId ? "hidden md:block" : "block"
							}`}
						>
							<ThreadList
								threads={threads.data}
								isPending={threads.isPending}
								error={threads.error}
								onRetry={() => void threads.refetch()}
								selectedId={selectedId}
								onSelect={open}
								onNewConversation={() => setPicking(true)}
							/>
						</section>
						<section
							className={`min-h-0 min-w-0 ${selectedId ? "block" : "hidden md:block"}`}
						>
							{!selectedId ? (
								<div className="flex h-full items-center justify-center p-8 text-center text-sm text-dashboard-muted">
									{t("realtor.messages.selectPrompt")}
								</div>
							) : contact ? (
								<Conversation contact={contact} onBack={close} />
							) : contactQuery.error ? (
								<div className="space-y-3 p-4" role="alert">
									<p className="text-sm text-dashboard-muted">
										{contactQuery.error.message ||
											t("realtor.messages.contactLoadFailed")}
									</p>
									<div className="flex flex-wrap gap-2">
										<StudioButton
											variant="secondary"
											size="xs"
											icon={RefreshCw}
											onClick={() => void contactQuery.refetch()}
										>
											{t("realtor.messages.retry")}
										</StudioButton>
										<StudioButton variant="quiet" size="xs" onClick={close}>
											{t("realtor.messages.back")}
										</StudioButton>
									</div>
								</div>
							) : (
								<div
									className="space-y-3 p-4"
									data-testid="messages-conversation-loading"
								>
									<Skeleton className="h-10 w-1/2 bg-dashboard-border" />
									<Skeleton className="h-40 w-full bg-dashboard-border" />
								</div>
							)}
						</section>
					</div>
				</Card>
			)}

			<NewConversationDialog
				open={picking}
				onOpenChange={setPicking}
				onPick={open}
			/>
			<ImportChatDialog open={importing} onOpenChange={setImporting} />
		</div>
	);
}

function EmptyInbox({
	onNewConversation,
	onImport,
}: {
	onNewConversation: () => void;
	onImport: () => void;
}) {
	const { t } = useTranslation();
	return (
		<Card className="border-dashboard-border bg-dashboard-bg shadow-[var(--dashboard-shadow-2)]">
			<CardContent className="space-y-4 p-8 text-center">
				<MessageSquare className="mx-auto h-5 w-5 text-dashboard-accent-dark" />
				<div className="space-y-1">
					<h2 className="font-display text-base italic font-medium text-dashboard-text">
						{t("realtor.messages.emptyTitle")}
					</h2>
					<p className="mx-auto max-w-md text-xs text-dashboard-muted">
						{t("realtor.messages.emptyBody")}
					</p>
				</div>
				<div className="flex flex-wrap items-center justify-center gap-2">
					<StudioButton size="sm" icon={Plus} onClick={onNewConversation}>
						{t("realtor.messages.newConversation")}
					</StudioButton>
					<StudioButton
						variant="secondary"
						size="sm"
						icon={FileUp}
						onClick={onImport}
					>
						{t("realtor.import.action")}
					</StudioButton>
					<Link
						to="/realtor/contacts"
						className="inline-flex h-9 items-center gap-1.5 rounded-[8px] border border-dashboard-border px-3.5 text-[13px] font-medium text-dashboard-text hover:bg-dashboard-surface"
					>
						<Users className="h-3.5 w-3.5" />
						{t("realtor.messages.goToContacts")}
					</Link>
				</div>
			</CardContent>
		</Card>
	);
}
